"use client";

import { type Provenance, type Quality, type SiteRecord, qualityExplanation, qualityTone } from "@/lib/site-energy";

/** A quality label that always says what it means on hover. */
export function QualityBadge({ quality, provenance }: { quality: Quality | string | null | undefined; provenance?: Provenance | string | null }) {
  const label = quality || "unmarked";
  return (
    <span className="inline-flex flex-wrap gap-1">
      <span
        title={qualityExplanation(label)}
        className={`inline-flex items-center rounded-full border px-2 py-0.5 text-[11px] font-semibold ${qualityTone(label)}`}
      >
        {label}
      </span>
      {provenance === "synthetic" ? (
        <span
          title="Synthetic demo data, not reported by a physical device. Provenance is separate from measurement quality."
          className="inline-flex items-center rounded-full border border-violet-500/30 bg-violet-500/15 px-2 py-0.5 text-[11px] font-semibold text-violet-300"
        >
          synthetic
        </span>
      ) : null}
    </span>
  );
}

export function SiteSelector({
  sites,
  value,
  onChange,
  eligible,
}: {
  sites: SiteRecord[];
  value: string;
  onChange: (siteId: string) => void;
  /** Sites that cannot serve this view are still listed, disabled, with the reason. */
  eligible: (site: SiteRecord) => string | null;
}) {
  return (
    <label className="flex flex-col gap-1 text-xs text-muted">
      Site
      <select
        value={value}
        onChange={(e) => onChange(e.target.value)}
        className="rounded-xl border border-border bg-surface px-3 py-2 text-sm text-foreground transition focus:border-accent/60 focus:outline-none focus:ring-2 focus:ring-accent/20"
      >
        {sites.map((site) => {
          const reason = eligible(site);
          return (
            <option key={site.site_id} value={site.site_id} disabled={reason !== null}>
              {site.name}
              {reason ? ` (${reason})` : ""}
            </option>
          );
        })}
      </select>
    </label>
  );
}

export function StatTile({ label, value, hint, quality, provenance }: { label: string; value: string; hint?: string; quality?: Quality | string | null; provenance?: Provenance | string | null }) {
  return (
    <div className="rounded-3xl border border-border bg-surface-raised p-5 shadow-xl transition-all duration-200 hover:-translate-y-0.5 hover:shadow-2xl">
      <div className="flex items-center justify-between gap-2">
        <div className="text-xs uppercase tracking-wide text-muted">{label}</div>
        {quality ? <QualityBadge quality={quality} provenance={provenance} /> : null}
      </div>
      <div className="mt-2 text-2xl font-semibold text-foreground">{value}</div>
      {hint ? <div className="mt-1 text-[11px] text-muted">{hint}</div> : null}
    </div>
  );
}

export function Notice({ tone, title, children }: { tone: "info" | "warn" | "error"; title: string; children?: React.ReactNode }) {
  // Info and warning boxes are not shown. Only genuine failures render.
  if (tone !== "error") return null;
  const cls =
    tone === "error"
      ? "border-rose-500/30 bg-rose-500/10 text-rose-300"
      : tone === "warn"
        ? "border-amber-500/30 bg-amber-500/10 text-amber-300"
        : "border-border bg-surface-raised text-foreground";
  return (
    <div className={`rounded-2xl border p-4 text-sm ${cls}`}>
      <div className="font-semibold">{title}</div>
      {children ? <div className="mt-1 text-xs opacity-90">{children}</div> : null}
    </div>
  );
}
